import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { MailCheck, MailX, Send, RefreshCw, ChevronDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATE = {
  valid: { label: "Key OK", tone: "#00863A" },
  missing: { label: "No API key", tone: "#B91C1C" },
  invalid: { label: "Key rejected", tone: "#B91C1C" },
  unreachable: { label: "Can't reach Resend", tone: "#B45309" },
  unknown: { label: "Unclear", tone: "#B45309" },
};

const Row = ({ k, v, mono }) => (
  <div className="flex items-baseline gap-2 text-[12px] py-0.5">
    <span className="text-ink-3 w-[112px] shrink-0">{k}</span>
    <span className={`text-ink font-semibold break-all ${mono ? "font-mono text-[11px]" : ""}`}>{v || "—"}</span>
  </div>
);

export default function EmailHealthCard() {
  const [h, setH] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setH((await api.get("/admin/email/health")).data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email/test");
      data.ok ? toast.success(`Test email sent to ${data.to} — check your inbox`)
              : toast.error(data.error || "Send failed", { duration: 9000 });
      load();
      setOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!h) return null;
  const st = STATE[h.key_check?.state] || STATE.unknown;
  const healthy = h.key_check?.state === "valid";

  return (
    <div className="glass p-4 mb-4" data-testid="email-health-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
          style={{ background: healthy ? "rgba(0,134,58,0.10)" : "rgba(185,28,28,0.10)", color: st.tone }}>
          {healthy ? <MailCheck size={17} /> : <MailX size={17} />}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display text-sm font-semibold flex items-center gap-2">
            Email delivery
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
              style={{ background: `${st.tone}1A`, color: st.tone }} data-testid="email-health-state">{st.label}</span>
          </div>
          <div className="text-[12px] text-ink-2 mt-0.5" data-testid="email-health-detail">
            {h.key_check?.detail} · sending as <b>{h.sender_email}</b> · {h.sent_count} delivered / {h.failed_count} failed
          </div>
        </div>
        <button onClick={sendTest} disabled={busy} className="btn-secondary" data-testid="email-test-btn">
          <Send size={13} className={busy ? "animate-pulse" : ""} /> Send test to me
        </button>
        <button onClick={load} className="btn-secondary" data-testid="email-health-refresh"><RefreshCw size={13} /></button>
        <button onClick={() => setOpen((o) => !o)} className="btn-secondary" data-testid="email-health-toggle">
          Details <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
        </button>
        <a href="/admin/email-log" className="btn-secondary" data-testid="email-health-full-log">Full log</a>
      </div>

      {h.domain_watch && h.domain_watch.status === "warning" && (
        <div className="mt-3 rounded-xl px-3 py-2 text-[12px] flex items-start gap-2"
          style={{ background: "rgba(180,83,9,0.10)", color: "#8a4408" }} data-testid="email-domain-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span><b>Sending domain needs attention</b> — {h.domain_watch.message}</span>
        </div>
      )}

      {open && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="rounded-xl p-3" style={{ background: "rgba(15,18,38,0.03)" }}>
            <div className="text-[10px] font-bold tracking-widest text-ink-3 uppercase mb-1.5">This environment</div>
            <Row k="API key" v={h.key_present ? h.key_hint : "not set"} mono />
            <Row k="Sender" v={`${h.sender_name} <${h.sender_email}>`} />
            <Row k="App name" v={h.app_name} />
            <Row k="Link base" v={h.app_base_url} mono />
            <Row k="Resend SDK" v={h.sdk} />
            {(h.key_check?.domains || []).map((d) => <Row key={d.name} k="Domain" v={`${d.name} · ${d.status}`} />)}
            {!h.key_present && (
              <div className="text-[11px] mt-2 leading-relaxed" style={{ color: "#B91C1C" }}>
                Add <b>RESEND_API_KEY</b> to this environment's variables and redeploy — every email is skipped until then.
              </div>
            )}
          </div>
          <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(15,18,38,0.08)" }}>
            <div className="text-[10px] font-bold tracking-widest text-ink-3 uppercase px-3 py-2"
              style={{ background: "rgba(15,18,38,0.03)" }}>Last {(h.recent || []).length} attempts</div>
            <div className="max-h-[190px] overflow-auto divide-y" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
              {(h.recent || []).map((e) => (
                <div key={e.id} className="px-3 py-1.5 text-[11px]" data-testid={`email-event-${e.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-bold" style={{ color: e.ok ? "#00863A" : "#B91C1C" }}>{e.ok ? "sent" : "failed"}</span>
                    <span className="font-mono text-ink-2 truncate">{e.to}</span>
                    <span className="ml-auto text-ink-3 font-mono">{(e.created_at || "").slice(5, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-ink-3 truncate">{e.subject}</div>
                  {!e.ok && <div className="mt-0.5" style={{ color: "#B91C1C" }}>{e.error}</div>}
                </div>
              ))}
              {!(h.recent || []).length && <div className="px-3 py-6 text-center text-[11px] text-ink-3">No emails attempted yet</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
